import { Button, StatusField } from '@amfintech/react-admin-ui';
import {
  CreateDataInput,
  CreateResourceEditDialogReturns,
  CreateResourceEditPageOptions,
  ResourceTableConfig,
  sentenceCase,
  useResourceAccess,
} from '@amfintech/refine-react-admin';
import dayjs from 'dayjs';

import { ModificationEntityType, UserStatus, User } from '~/api';
import { useSendInvitationCodeMutation } from '~/hooks/user-send-invitation-code';
import { formatUserStatus } from '~/resources/customer-resource';
import { resourceNames } from '~/resources/resource-names';

export const ProfileMaintenancePage = ({
  createTableComponent,
  createResourceEditDialog,
  profileId,
}: {
  createTableComponent: <T extends object>(props: ResourceTableConfig<T>) => () => React.ReactNode;
  createResourceEditDialog: <T extends object, DefaultValues extends CreateDataInput<T>>(
    options: CreateResourceEditPageOptions<T, DefaultValues>
  ) => CreateResourceEditDialogReturns;
  profileId: string;
}) => {
  const { mutate: sendInvitationCode } = useSendInvitationCodeMutation();
  const { canWrite } = useResourceAccess(resourceNames.profile);

  const handleGenerateInvitationCode = async (userId: string) => {
    sendInvitationCode(userId);
  };

  const TableComponent = createTableComponent<User>({
    resource: resourceNames.user,
    fields: [
      'id',
      'name',
      'username',
      'email',
      'phoneNo',
      'hpPhoneNo',
      'status',
      'updatedAt',
      'invitationCode',
      'invitationSentAt',
      'onboardedAt',
    ],
    columns: () => [
      {
        field: 'username',
        header: 'Username',
        accessorKey: 'username',
      },
      {
        field: 'name',
        header: 'Full Name',
        accessorKey: 'name',
      },
      {
        field: 'email',
        header: 'Email',
        accessorKey: 'email',
      },
      {
        field: 'phoneNo',
        header: 'Office Phone No',
        accessorKey: 'phoneNo',
      },
      {
        field: 'hpPhoneNo',
        header: 'HP Phone No',
        accessorKey: 'hpPhoneNo',
      },
      {
        field: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: (data) => {
          const status = data.cell.getValue<UserStatus>();
          return <StatusField status={formatUserStatus(status)} label={status} />;
        },
      },
      // {
      //   field: 'updatedAt',
      //   header: 'Last Modified',
      //   accessorKey: 'updatedAt',
      //   cell: ({ row }) => dayjs(row.original.updatedAt).format('DD MMM YYYY HH:mm:ssa'),
      // },
      {
        field: 'action',
        header: 'Invitation Code',
        cell: (data) => {
          // Check user status is ACTIVE, do not show invitation
          if (data.row.original.status === UserStatus.Active) {
            return null;
          }

          const userId = data.row.original.id;

          if (!userId) return null;

          if (data.row.original.invitationSentAt) {
            return (
              <div className="flex flex-row items-center gap-2">
                <Button
                  disabled={!canWrite}
                  variant="solid"
                  size="sm"
                  className="min-w-[6rem]"
                  onClick={() => handleGenerateInvitationCode(userId)}
                >
                  Resend
                </Button>
                <div>
                  <div>{data.row.original.invitationCode} </div>
                  <p className="text-sm text-muted-foreground">
                    Sent at{' '}
                    {dayjs(data.row.original.invitationSentAt).format('DD MMM YYYY HH:mm:ssa')}
                  </p>
                </div>
              </div>
            );
          }

          return (
            <Button
              disabled={!canWrite}
              variant="solid"
              size="sm"
              className="min-w-[6rem]"
              onClick={() => handleGenerateInvitationCode(userId)}
            >
              Invitation Code
            </Button>
          );
        },
      },
      {
        field: 'onboardedAt',
        header: 'Onboarded At',
        accessorKey: 'onboardedAt',
        cell: ({ getValue }) =>
          getValue() ? dayjs(getValue<string>()).format('DD MMM YYYY, h:mm A') : '',
      },
      {
        field: 'action',
        header: 'Action',
        cell: (data) => {
          const EditButton = createResourceEditDialog<User, CreateDataInput<User>>({
            resourceName: resourceNames.user,
            fields: ['id', 'username', 'email', 'name', 'phoneNo', 'hpPhoneNo', 'status'],
            defaultValues: {
              email: data.row.original.email,
              name: data.row.original.name,
              phoneNo: data.row.original.phoneNo ?? '',
              hpPhoneNo: data.row.original.hpPhoneNo ?? '',
              status: data.row.original.status,
            },
            controls: {
              components: {
                profileId: {
                  type: 'text',
                  config: {
                    label: 'Profile ID',
                    defaultValue: profileId,
                    disabled: true,
                    hideLabel: true,
                    hidden: true,
                  },
                },
                email: {
                  type: 'text',
                  config: {
                    label: 'Email',
                    type: 'email',
                    disabled: true,
                  },
                },
                name: {
                  type: 'text',
                  config: {
                    label: 'Full Name',
                    disabled: true,
                  },
                },
                phoneNo: {
                  type: 'text',
                  config: {
                    label: 'Office Phone No',
                    placeholder: 'Enter phone number',
                    inputMode: 'tel',
                    pattern: '^[0-9]+$',
                    patternMessage: 'Only digits are allowed',
                    disabled: data.row.original.status === UserStatus.Closed,
                  },
                },
                hpPhoneNo: {
                  type: 'text',
                  config: {
                    label: 'HP Phone No',
                    placeholder: 'Enter HP phone number',
                    inputMode: 'tel',
                    pattern: '^[0-9]+$',
                    patternMessage: 'Only digits are allowed',
                    disabled: data.row.original.status === UserStatus.Closed,
                  },
                },
                status: {
                  type: 'select',
                  config: {
                    label: 'Status',
                    options: Object.values(UserStatus).map((status) => ({
                      label: sentenceCase(status),
                      value: status,
                    })),
                    disabled: data.row.original.status === UserStatus.Closed,
                  },
                },
              },
            },
            makerChecker: {
              entityType: ModificationEntityType.User,
            },
            confirmBeforeSubmit: true,
            confirmationTitle: 'Maintenance User Update Confirmation',
            confirmationDescription: 'Please confirm that you want to update this maintenance user.',
          }).Button;
          return (
            <EditButton
              disabled={!canWrite}
              resourceId={data.row.original.id}
              variant="solid"
              size="sm"
              className="min-w-[6rem]"
            />
          );
        },
      },
    ],
    filterControls: {
      email: {
        type: 'text',
        config: {
          label: 'Email',
          type: 'email',
        },
        operator: 'contains',
      },
    },
    filterConfig: {
      permanent: [
        {
          field: '.profiles.some.id' as any,
          operator: 'eq',
          value: profileId,
        },
      ],
    },
  });

  return <TableComponent />;
};
